import { CameraView, useCameraPermissions } from 'expo-camera'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Linking, Platform, StyleSheet, useWindowDimensions, View } from 'react-native'
import { textStyles } from 'styles/text'
import i18n from 'utils/i18n'
import Text from "theme/Text";
import Button from 'theme/Button'
import HelperString from 'utils/helper-string'
import { themedCameraStyle } from 'styles/components/atoms/ThemedCamera'
import HelperDevelopment from 'utils/helper-development'
import LoadingComponent from 'components/atoms/Loading'
import { colorList } from 'utils/colors'
import { ThemedValues } from 'styles/themed'

export const cameraFacing = {
	BACK: 'back',
	FRONT: 'front'
}

const ThemedCamera = ({ facing = cameraFacing.BACK, ...props }) => {
	
	const { height, width } = useWindowDimensions()

	const camera = useRef();

	const [ permission, requestPermission ] = useCameraPermissions();
	const canOpenAppSettings = Platform.OS !== 'web';
	
	// Screen Ratio and image padding
	const [ ratio, setRatio ] = useState( '4:3' )
	const [ cameraWidth, setCameraWidth ] = useState( width )
	const [ isCameraReady, setIsCameraReady ] = useState( false )
	const [ isScanned, setIsScanned ] = useState( false );
	
	// create a throttled function to handle handleOnBarcodeScanned
	const isScannedRef = useRef( false );
	
	const onPressSettings = async() => {
		try {
			await Linking.openSettings();
		} catch ( error ) {
			console.warn( 'Could not open camera permission settings', error );
		}
	}
	
	const handleOnBarcodeScanned = ( result ) => {
		if( isScannedRef.current ) {
			return;
		}
		
		isScannedRef.current = true;
		setIsScanned( true )

		if( props.onBarCodeScanned ) {
			props.onBarCodeScanned( result, handleOnBarcodeScannedStop )
		} else {
			handleOnBarcodeScannedStop()
		}
	};
	
	const handleOnBarcodeScannedStop = () => {
		setIsScanned( false )
		isScannedRef.current = false;
	}

	const onRequestCameraPermission = async () => {
		requestPermission().finally( () => {
			console.log('Camera request requested')
		});
	}

	useEffect(() => {
		requestPermission().finally( () => {
			console.log('Camera request completed')
		});

	}, [] )
	
	const content = useMemo( () => {
		const content = [];
		if( permission?.status !== 'granted' ) {
			content.push(
				<Text
					key={ HelperString.hash('themed-camera-text-1') }
					style={ textStyles.textCenter }>
					
					{ i18n.t('permission.camera.request_permission') }
				</Text>
			)
			
			content.push(
				<Text
					key={ HelperString.hash('themed-camera-text-2') }
					style={ textStyles.textCenter }>
					
					{ i18n.t('permission.camera.no_permission') }
				</Text>
			);
			
			if( canOpenAppSettings ) {
				content.push(
					<Button
						key={ HelperString.hash('themed-camera-button') }
						onPress={ onPressSettings }>
						
						{ i18n.t('permission.camera.settings_button') }
					</Button>
				)
			}
			
			if( HelperDevelopment.isDev() ) {
				content.push(
					<Button
						key={ HelperString.hash('themed-camera-button-request') }
						onPress={ onRequestCameraPermission }>
						
						{ i18n.t('permission.camera.request_button') }
					</Button>
				)
			}
			
			return (
				<View
					style={styles.viewFlex1JustifyContentCenter}>
					
					{ content }
				</View>
			)
		}
		
		return null
	}, [ permission, isCameraReady ])
	
	return (
		<View
			style={ themedCameraStyle.container }>
			
			{ content }
			
			{ permission?.status === 'granted' && (
				<CameraView
					{ ...props }
					ref={ camera }
					style={[
						themedCameraStyle.cameraPreview
					]}
					autofocus="on"
					facing={ facing }
					ratio={ ratio }
					zoom={ 0 }
					focusDepth={ 1 }
					barcodeScannerSettings={{
						barcodeTypes: [
							'qr'
						]
					}}
					onCameraReady={() => {
						setIsCameraReady( true )
					}}
					onBarcodeScanned={ handleOnBarcodeScanned }
					onLayout={(event) => {
						const { width, height } = event.nativeEvent.layout;
	
						if( width !== cameraWidth ) {
							setCameraWidth( width )
						}
					}}
				/>
			)}

			{ isScanned && (
				<View style={ styles.loadingContainer }>
					<LoadingComponent />
				</View>
			)}
		</View>
	)
}

const styles = StyleSheet.create( {
	viewFlex1JustifyContentCenter: {
		flex: 1,
		justifyContent: 'center',
		flexDirection: 'column',
		backgroundColor: colorList.PAGE.primary,
		padding: ThemedValues.marginHorizontal,
	},
	loadingContainer: {
		position: 'absolute',
		alignSelf: 'center',
		justifyContent: 'center',
		flex: 1,
	}
});

export default ThemedCamera
